import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { 
  Sparkles,
  User,
  Building,
  Mail
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: any;
  generatedQuestions: any[];
  dialogType: 'survey' | 'template';
}

const ContactDialog: React.FC<ContactDialogProps> = ({
  open,
  onOpenChange,
  config,
  generatedQuestions,
  dialogType
}) => {
  const { toast } = useToast();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const handleContactSubmit = async () => {
    if (!contactForm.name || !contactForm.email || !contactForm.company) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('send-contact-request', {
        body: {
          name: contactForm.name,
          email: contactForm.email,
          company: contactForm.company,
          message: contactForm.message,
          surveyConfig: config,
          questionsCount: generatedQuestions.length,
        },
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Request Sent Successfully!",
        description: "Your request has been sent to the JiGSO team. We'll be in touch within 24-48 hours!",
      });
      
      onOpenChange(false);
      setContactForm({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Send Failed",
        description: "Failed to send your request. Please try again or contact us directly.",
        variant: "destructive",
      });
    }
  };

  const getDialogTitle = () => {
    return dialogType === 'survey' ? 'Try Survey in JiGSO Platform' : 'Try Template in JiGSO Platform';
  };

  const getDialogDescription = () => {
    return dialogType === 'survey' 
      ? 'Interested in using this survey in the full JiGSO platform? Fill out the form below and our team will get in touch with you.'
      : 'Interested in using this conversation template in the full JiGSO platform? Fill out the form below and our team will get in touch with you.';
  };

  const getMessagePlaceholder = () => {
    return dialogType === 'survey' 
      ? 'Tell us about your survey needs (optional)'
      : 'Tell us about your conversation template needs (optional)';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            {getDialogTitle()}
          </DialogTitle>
          <DialogDescription>
            {getDialogDescription()}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="contact-name" className="flex items-center gap-1">
              <User className="w-4 h-4" />
              Full Name *
            </Label>
            <Input
              id="contact-name"
              value={contactForm.name}
              onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter your full name"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="contact-email" className="flex items-center gap-1">
              <Mail className="w-4 h-4" />
              Email Address *
            </Label>
            <Input
              id="contact-email"
              type="email"
              value={contactForm.email}
              onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
              placeholder="Enter your email address"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="contact-company" className="flex items-center gap-1">
              <Building className="w-4 h-4" />
              Company *
            </Label>
            <Input
              id="contact-company"
              value={contactForm.company}
              onChange={(e) => setContactForm(prev => ({ ...prev, company: e.target.value }))}
              placeholder="Enter your company name"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="contact-message">Additional Message</Label>
            <Textarea
              id="contact-message"
              value={contactForm.message}
              onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
              placeholder={getMessagePlaceholder()}
              rows={3}
            />
          </div>
          
          <div className="flex gap-2 pt-4">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleContactSubmit}
              className="flex-1 bg-gradient-primary text-white"
            >
              Send Request
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDialog; 